import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { Observable, Subject, from } from 'rxjs';
import { Platform } from '@ionic/angular';
import firebase from 'firebase/app';
import { cfaSignIn, mapUserToUserInfo } from 'capacitor-firebase-auth';
import { AngularFirestore, AngularFirestoreDocument } from '@angular/fire/firestore';
import { ProfileModel } from '../profile.model';

@Injectable()
export class FirebaseAuthService {

  currentUser: firebase.User;
  userProviderAdditionalInfo: any;
  redirectResult: Subject<any> = new Subject<any>();

  constructor(
    public angularFire: AngularFireAuth,
    public platform: Platform,
    public afs: AngularFirestore
  ) {

    this.angularFire.authState.subscribe(user => {
      if (user) {
        this.currentUser = user;
        localStorage.setItem('user', JSON.stringify(this.currentUser));
        JSON.parse(localStorage.getItem('user'));
      } else {
        localStorage.setItem('user', null);
        JSON.parse(localStorage.getItem('user'));
      }
    })
  }

  ngOnInit() {
    this.angularFire.authState.subscribe(user => {
      if (user) {
        this.currentUser = user;
        this.redirectResult.next(user);
      } else {
        console.log('No user logged in as of now');
      }
    })
  }

  // Returns true when user's email is verified
  get isEmailVerified(): boolean {
    const user = JSON.parse(localStorage.getItem('user'));
    return (user.emailVerified !== false) ? true : false;
  }

  getRedirectResult(): Observable<any> {
    return this.redirectResult.asObservable();
  }

  setProviderAdditionalInfo(additionalInfo: any) {
    this.userProviderAdditionalInfo = { ...additionalInfo };
  }

  public getProfileData() {
    const userModel = new ProfileModel();
    this.currentUser = firebase.auth().currentUser;
    console.log("getProfileData: " + JSON.stringify(this.currentUser))
    let providerData: any = this.currentUser.providerData[0];
    console.log("getProfileData: " + JSON.stringify(providerData))
    if (this.userProviderAdditionalInfo) {
      providerData = { ...providerData, ...this.userProviderAdditionalInfo };
    }

    // Default imgs are too small and our app needs a bigger image
    switch (providerData.providerId) {
      case 'facebook.com':
        userModel.image = providerData.photoURL + '?height=400';
        break;
      case 'password':
        userModel.image = 'https://s3-us-west-2.amazonaws.com/ionicthemes/otros/avatar-placeholder.png';
        break;
      case 'twitter.com':
        userModel.image = providerData.photoURL.replace('_normal', '_400x400');
        break;
      case 'google.com':
        userModel.image = providerData.photoURL.split('=')[0];
        break;
      default:
        userModel.image = providerData.photoURL;
    }
    userModel.name = providerData.name || providerData.displayName || 'What\'s your name?';
    userModel.role = 'How would you describe yourself?';
    userModel.description = providerData.description || 'Anything else you would like to share with the world?';
    userModel.phoneNumber = providerData.phoneNumber || 'Is there a number where I can reach you?';
    userModel.email = providerData.email || 'Where can I send you emails?';
    userModel.emailVerified = this.currentUser.emailVerified || false;
    userModel.provider = (providerData.providerId !== 'password') ? providerData.providerId : 'Credentials';
    userModel.stopPubMensuel = providerData.stopPubMensuel || false;
    userModel.stopPubAnnuel = providerData.stopPubAnnuel || false;
    userModel.dateDebutStopPub = providerData.dateDebutStopPub || null;
    userModel.token = providerData.token || null;
    localStorage.setItem('user', JSON.stringify(userModel));

    return userModel;
  }

  // Get the currently signed-in user
  getLoggedInUser() {
    // alert("getLoggedInUser : " + JSON.stringify(this.currentUser));
    return this.currentUser;
  }

  signOut(): Observable<any> {
    return from(this.angularFire.signOut());
  }

  signInWithEmail(email: string, password: string): Promise<firebase.auth.UserCredential> {
    return this.angularFire.signInWithEmailAndPassword(email, password);
  }

  // Login in with email/password
  SignIn(email, password) {
    return firebase.auth().signInWithEmailAndPassword(email, password)
  }

  signUpWithEmail(email: string, password: string): Promise<firebase.auth.UserCredential> {
    return this.angularFire.createUserWithEmailAndPassword(email, password);
  }

  socialSignIn(providerName: string, scopes?: Array<string>): Promise<any> {
    const provider = new firebase.auth.OAuthProvider(providerName);

    // add any permission scope you need
    if (scopes) {
      scopes.forEach(scope => {
        provider.addScope(scope);
      });
    }

    if (this.platform.is('desktop')) {
      return this.angularFire.signInWithPopup(provider);
    } else {
      // web but not desktop, for example mobile PWA
      return this.angularFire.signInWithRedirect(provider);
    }
  }

  signInWithFacebook() {
    const provider = new firebase.auth.FacebookAuthProvider();
    return this.socialSignIn(provider.providerId);
  }

  signInWithGoogle() {
    return cfaSignIn('google.com').pipe(
      mapUserToUserInfo(),
    )
  }

  signInWithTwitter() {
    const provider = new firebase.auth.TwitterAuthProvider();
    return this.socialSignIn(provider.providerId);
  }

  doRegister(value) {
    return new Promise<any>((resolve, reject) => {
      firebase.auth().createUserWithEmailAndPassword(value.email, value.password)
        .then(
          res => resolve(res),
          err => reject(err))
    })
  }

  doLogin(value) {
    return new Promise<any>((resolve, reject) => {
      firebase.auth().signInWithEmailAndPassword(value.email, value.password)
        .then(
          res => resolve(res),
          err => reject(err))
    })
  }

  logout() {
    this.angularFire.signOut();
  }

  // Email verification when new user register
  SendVerificationMail() {
    return firebase.auth().currentUser.sendEmailVerification();
  }

  // Register user with email/password
  RegisterUser(email, password) {
    return firebase.auth().createUserWithEmailAndPassword(email, password)
  }

  async createFirebaseuser(appleResponse): Promise<any> {
    // Create a custom OAuth provider    
    const provider = new firebase.auth.OAuthProvider('apple.com');
    // Create sign in credentials with our token
    const credential = provider.credential({
      idToken: appleResponse.identityToken
    });
    
    // Call the sign in with our created credentials
    // const userCredential = await this.angularFire.signInWithCredential(credential);
    // return userCredential;
    return this.angularFire.signInWithCredential(credential);

    // Update the user document in Firestore
    // this.updateUserData(userCredential.user, appleResponse.givenName, appleResponse.familyName);
  }

  // async updateUserData(user, firstName, lastName) {
  //   const userRef: AngularFirestoreDocument = this.afs.doc(`users/${user.uid}`);

  //   let data = {
  //     email: user.email
  //   };

  //   // Only add names if they are set, otherwise we would override them
  //   if (firstName) {
  //     data['first_name'] = firstName;
  //   }

  //   if (lastName) {
  //     data['last_name'] = lastName;
  //   }
    
  //   return userRef.set(data, { merge: true });
  // }


}
